import { ShieldStar } from "@phosphor-icons/react";

/**
 * Colour-coded SLA grade shield for a vendor. Uses cached fields:
 *   - sla_grade: "Excellent" | "Good" | "Fair" | "Slow"
 *   - sla_color: hex color
 *   - sla_response_count: number of responded tasks (null = unrated)
 *   - sla_avg_response_seconds: average assigned → started delta
 *
 * When the supplier has no cached grade (fresh tenant), renders a subtle
 * "Unrated" pill rather than disappearing silently.
 */
const GRADE_META = {
  Excellent: { bg: "bg-emerald-500/15", fg: "text-emerald-700 dark:text-emerald-400", ring: "ring-emerald-500/30" },
  Good:      { bg: "bg-sky-500/15",     fg: "text-sky-700 dark:text-sky-400",         ring: "ring-sky-500/30" },
  Fair:      { bg: "bg-amber-500/15",   fg: "text-amber-700 dark:text-amber-500",     ring: "ring-amber-500/30" },
  Slow:      { bg: "bg-rose-500/15",    fg: "text-rose-700 dark:text-rose-400",       ring: "ring-rose-500/30" },
};

function _formatResponseTime(seconds) {
  if (seconds == null || seconds < 0) return "";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} h`;
  return `${(seconds / 86400).toFixed(1)} d`;
}

export default function SlaShield({ vendor, size = "sm", showLabel = true, className = "" }) {
  const grade = vendor?.sla_grade;
  const count = vendor?.sla_response_count || 0;
  const avg = vendor?.sla_avg_response_seconds;

  if (!grade || count === 0) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10px] text-muted-foreground ${className}`}
        title="No response history yet — grade appears after the first jobs"
        data-testid={`sla-shield-${vendor?.id || "unknown"}`}
      >
        <ShieldStar size={size === "lg" ? 14 : 11} weight="duotone" />
        Unrated
      </span>
    );
  }

  const meta = GRADE_META[grade] || GRADE_META.Fair;
  const avgLabel = _formatResponseTime(avg);
  const tip = `SLA: ${grade}${avgLabel ? ` · avg response ${avgLabel}` : ""} · ${count} job${count === 1 ? "" : "s"} tracked`;

  return (
    <span
      className={`inline-flex items-center gap-1 font-semibold ring-1 ${meta.bg} ${meta.fg} ${meta.ring} rounded-full ${
        size === "lg" ? "text-xs px-2.5 py-1" : "text-[10px] px-2 py-0.5"
      } ${className}`}
      title={tip}
      data-testid={`sla-shield-${vendor.id}`}
    >
      <ShieldStar size={size === "lg" ? 14 : 11} weight="fill" />
      {showLabel && grade}
      {showLabel && avgLabel && (
        <span className={`font-normal opacity-80 ${size === "lg" ? "text-[10px]" : "text-[9px]"}`}>· {avgLabel}</span>
      )}
    </span>
  );
}
